/* eslint-disable react-hooks/exhaustive-deps */
import ReloadIcon from "@mui/icons-material/Replay";
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { validate } from "validate.js";
import { fetchRoles, showSnackbar } from "../../app/slices/commonSlice";
import { addUser, updateUser } from "../../app/slices/usersSlice";
import { hasAccess } from "../../app/utils";

const UserActionDialog = ({ data = null, open, handleClose }) => {
  const dispatch = useDispatch();

  const {
    put: { data: dataPut, error: errorPut, status: statusPut },
    post: { data: dataPost, error: errorPost, status: statusPost },
  } = useSelector(({ users }) => users);
  const {
    roles: { data: roles },
  } = useSelector(({ common }) => common);
  const [formValues, setFormValues] = useState({});
  const [errors, setErrors] = useState([]);
  const statuses = [
    {
      value: "A",
      label: "Active",
    },
    {
      value: "I",
      label: "Inactive",
    },
    {
      value: "D",
      label: "Disabled",
    },
  ];
  //* use effect

  useEffect(() => {
    if (data) {
      setFormValues({
        email: data?.EMAIL,
        phoneNumber: data?.PHONE_NUMBER,
        role: data?.ROLE_ID,
        fullName: data?.FULL_NAME,
        incorporationNumber: data?.INCORPORATION_NUMBER,
        status: data?.STATUS,
      });
    }
    dispatch(fetchRoles({ status: "active" }));
  }, [data, dispatch]);

  useEffect(() => {
    if (dataPut || dataPost) {
      dispatch(
        showSnackbar({
          open: true,
          message: "Changes saved successfully.",
          type: "success",
        })
      );
      handleClose(true);
    }
  }, [dataPut, dataPost]);

  useEffect(() => {
    if (errorPost || errorPut) {
      dispatch(
        showSnackbar({
          open: true,
          message:
            errorPost?.message || errorPut?.message || "An error occurred.",
          type: "error",
        })
      );
    }
  }, [errorPost, errorPut]);

  //* builders
  const handleSubmit = () => {
    let _errors = validate(formValues, {
      email: {
        presence: { allowEmpty: false },
        email: true,
      },
      phoneNumber: {
        presence: { allowEmpty: false },
      },
      fullName: {
        presence: { allowEmpty: false },
      },
      role: {
        presence: { allowEmpty: false },
      },
    });

    if (_errors) {
      setErrors(_errors);
    } else {
      setErrors([]);
      let payload = {
        EMAIL: formValues?.email?.toLowerCase(),
        PHONE_NUMBER: formValues?.phoneNumber,
        ROLE_ID: formValues?.role,
        FULL_NAME: formValues?.fullName.toUpperCase(),
        CATEGORY: "INTERNAL",
        INSTITUTION_ID: 1,
        STATUS: formValues?.status,
      };
      if (data) {
        dispatch(updateUser({ id: data?.USER_ID, payload: payload }));
      } else {
        dispatch(addUser(payload));
      }
    }
  };

  const handleChange = (e) => {
    let inputName = e?.target?.name;
    let inputValue = e?.target?.value;

    setFormValues({
      ...formValues,
      [inputName]: inputValue,
    });
  };

  return (
    <Dialog open={open}>
      <DialogTitle>
        <span>
          <b>{` ${data ? "Update " : "Create "}User`}</b>
        </span>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          <Grid container>
            {formValues?.incorporationNumber && (
              <Grid item md={6} sm={12} xs={12} className="padded-grid-item">
                <TextField
                  key={"input-incorporationNumber"}
                  fullWidth
                  disabled
                  margin="dense"
                  variant="outlined"
                  name="incorporationNumber"
                  id="incorporationNumber"
                  label="Incorporation Number"
                  autoComplete="new-password"
                  onChange={handleChange}
                  value={formValues?.incorporationNumber || ""}
                />
              </Grid>
            )}
            <Grid item md={6} sm={12} xs={12} className="padded-grid-item">
              <TextField
                key={"input-email"}
                required
                fullWidth
                margin="dense"
                variant="outlined"
                error={errors["email"] ? true : false}
                helperText={errors["email"] ? errors.email[0] : null}
                name="email"
                id="email"
                label="Email Address"
                autoComplete="new-password"
                onChange={handleChange}
                value={formValues?.email || ""}
              />
            </Grid>

            <Grid item md={6} sm={12} xs={12} className="padded-grid-item">
              <TextField
                key={"input-fullName"}
                required
                fullWidth
                margin="dense"
                variant="outlined"
                error={errors["fullName"] ? true : false}
                helperText={errors["fullName"] ? errors.fullName[0] : null}
                name="fullName"
                id="fullName"
                label="Full Name"
                autoComplete="new-password"
                onChange={handleChange}
                value={formValues?.fullName?.toUpperCase() || ""}
              />
            </Grid>

            <Grid item md={6} sm={12} xs={12} className="padded-grid-item">
              <Autocomplete
                id="role"
                fullWidth
                onChange={(e, value) => {
                  if (value) e.preventDefault();
                  handleChange({
                    target: { name: "role", value: value?.ROLE_ID },
                  });
                }}
                options={roles?.filter((x) => x?.ROLE_CODE !== "APPLICANT")}
                getOptionLabel={(option) => option.ROLE_NAME}
                value={
                  roles?.find((r) => r.ROLE_ID === formValues?.role) || null
                }
                noOptionsText={
                  <div style={{ display: "flex", flexDirection: "row" }}>
                    <span style={{ width: "98%" }}>{"No options"}</span>
                    <IconButton
                      size="small"
                      id="clientReloadIcon"
                      aria-label="client icon"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        dispatch(fetchRoles({ status: "active" }));
                      }}
                    >
                      <ReloadIcon className="app-button-icon" fontSize="12px" />
                    </IconButton>
                  </div>
                }
                renderInput={(params) => (
                  <TextField
                    required
                    margin="dense"
                    name="role"
                    autoComplete="new-password"
                    fullWidth
                    error={errors["role"] ? true : false}
                    helperText={errors["role"] ? errors["role"][0] : null}
                    {...params}
                    label="Role"
                    variant="outlined"
                  />
                )}
              />
            </Grid>

            <Grid item md={6} sm={12} xs={12} className="padded-grid-item">
              <TextField
                key={"input-mobile-phone"}
                required
                margin="dense"
                fullWidth
                autoComplete="new-password"
                variant="outlined"
                error={errors["phoneNumber"] ? true : false}
                helperText={
                  errors["phoneNumber"] ? errors.phoneNumber[0] : null
                }
                name="phoneNumber"
                id="mobile-phone"
                label="Mobile Phone"
                onChange={handleChange}
                value={formValues?.phoneNumber || ""}
              />
            </Grid>

            {data&&<Grid item md={6} sm={12} xs={12} className="padded-grid-item">
              <TextField
                key={"status"}
                disabled={!data}
                required
                margin="dense"
                name="status"
                select
                label="Status"
                id="user-status"
                fullWidth
                variant="outlined"
                value={formValues.status || ""}
                onChange={handleChange}
              >
                {statuses.map((option) => {
                  return (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Grid>}
          </Grid>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Grid
          item
          md={12}
          className="flex-row justify-content-space-between"
          style={{ width: "100%", margin: "0px 10px" }}
        >
          <Grid item lg={6} style={{ width: "49%" }}></Grid>

          <Grid item lg={6} className="padded-grid-item">
            {hasAccess({ type: "name", resource: "EDIT_USERS" }) && (
              <Button
                size="small"
                id="btn-submit"
                margin="dense"
                disabled={statusPost === "loading" || statusPut === "loading"}
                color="primary"
                variant="contained"
                onClick={handleSubmit}
              >
                {(statusPost === "loading" || statusPut === "loading") && (
                  <CircularProgress
                    size={18}
                    style={{
                      marginRight: "5px",
                    }}
                  />
                )}
                Submit
              </Button>
            )}

            <Button
              size="small"
              id="btn-close"
              disabled={statusPost === "loading" || statusPut === "loading"}
              margin="dense"
              color="secondary"
              variant="contained"
              onClick={handleClose}
              style={{ marginLeft: "10px" }}
            >
              Close
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
};

export default UserActionDialog;
